// SPDX-License-Identifier: GPL-3.0-or-later
// The boss of a stage. A stage ends with a boss SigmaBot. The player beats it to clear the stage.
//
// The rules of the fight:
// - The boss HP grows with the stage number.
// - The phase comes from the HP share: 0 above 75%, 1 at or below 75%, 2 at or below 50%,
//   3 at or below 25%.
// - A boss attack is a shield slam. A wind-up (the telegraph) runs first. Then the shield turns on.
//   The shield blocks all damage. The boss attacks faster in each phase.
// - A phase change interrupts a wind-up. It turns the shield on for a short time.
// - A boss attack never drains a ball, never locks a flipper and never takes score.
//
// The boss stands on an existing target group. It adds no collider and no physics.
// Pure: no clock, no random source, no I/O. Time comes in as game ms (dt).
// A tick allocates nothing. Only an edge (start, hit, phase, wind-up, shield, defeat) sends one
// small event object.
//
// The sigmashake-horde boss is the model of this fight. No code is copied from it.

import type { BossHitSource, BossState } from "./types.ts";

/** The target group that the boss stands on. The group is DENY: four standup targets in a row. */
export const BOSS_ANCHOR = "group:deny";

/** The names of the boss SigmaBots. The name of stage N is entry N (the list repeats if needed). */
export const BOSS_NAMES: readonly string[] = [
  "Ember Bean",
  "Glitch Pip",
  "Static Nub",
  "Rusty Cog",
  "Grand Widget",
];

/** The boss HP of stage 1, and the HP that each later stage adds. */
export const BOSS_HP_BASE = 50;
export const BOSS_HP_PER_STAGE = 20;

/** The damage of one ball hit, by source. A source that is not here does no damage. */
export const BOSS_DAMAGE: Readonly<Record<BossHitSource, number>> = {
  bumper: 1,
  target: 2,
  ramp: 5,
  orbit: 5,
};

/** The HP share at or below which phase 1, 2 and 3 start. */
export const BOSS_PHASE_SHARE: readonly [number, number, number] = [0.75, 0.5, 0.25];

/** The wind-up of a shield slam, in game ms. */
export const BOSS_TELEGRAPH_MS = 1_200;

/** The time that the shield of a slam stays on, in game ms. */
export const BOSS_SLAM_SHIELD_MS = 3_000;

/** The time that the shield stays on after a phase change, in game ms. */
export const BOSS_PHASE_SHIELD_MS = 1_500;

/** The wait before the next wind-up, in game ms, by phase. A higher phase waits less. */
export const BOSS_WAIT_MS: readonly [number, number, number, number] = [9_000, 7_000, 5_500, 4_000];

/** The highest boss phase. */
export const BOSS_MAX_PHASE = 3;

// ───────────────────────────── events ─────────────────────────────

/** The cause of a shield. */
export type BossShieldCause = "phase" | "slam";

/**
 * The feedback events of the boss. The shape of each event is the shape in
 * `src/shared/feedback/events.ts`.
 */
export type BossFeedbackEvent =
  | {
      readonly kind: "boss_start";
      readonly name: string;
      readonly stage: number;
      readonly hp: number;
      readonly maxHp: number;
      readonly anchor: string;
    }
  | {
      readonly kind: "boss_hit";
      readonly damage: number;
      readonly hp: number;
      readonly maxHp: number;
      readonly blocked: boolean;
      readonly source: BossHitSource;
    }
  | {
      readonly kind: "boss_phase";
      readonly phase: number;
      readonly hp: number;
      readonly maxHp: number;
    }
  | { readonly kind: "boss_telegraph"; readonly ms: number }
  | {
      readonly kind: "boss_shield";
      readonly on: boolean;
      readonly ms: number;
      readonly cause: BossShieldCause;
    }
  | { readonly kind: "boss_defeat"; readonly name: string; readonly stage: number };

/** Where the boss sends its events. */
export interface BossSink {
  emit(ev: BossFeedbackEvent): void;
}

/** The result of one hit on the boss. */
export type BossHitResult = "none" | "blocked" | "hit" | "defeated";

// ───────────────────────────── pure helpers ─────────────────────────────

/** The boss HP of a stage. The stage number is 1-based. A bad number counts as stage 1. */
export function bossMaxHp(stageNumber: number): number {
  const stage = Number.isFinite(stageNumber) ? Math.max(1, Math.trunc(stageNumber)) : 1;
  return BOSS_HP_BASE + BOSS_HP_PER_STAGE * (stage - 1);
}

/** The boss name of a stage. The stage number is 1-based. */
export function bossNameOf(stageNumber: number): string {
  const stage = Number.isFinite(stageNumber) ? Math.max(1, Math.trunc(stageNumber)) : 1;
  return BOSS_NAMES[(stage - 1) % BOSS_NAMES.length] ?? "Sigma Bot";
}

/** The boss phase from its HP share. Phase 0 is full health. Phase 3 is the last quarter. */
export function bossPhaseOf(hp: number, maxHp: number): number {
  if (!(maxHp > 0)) return 0;
  const share = hp / maxHp;
  if (share <= BOSS_PHASE_SHARE[2]) return 3;
  if (share <= BOSS_PHASE_SHARE[1]) return 2;
  return share <= BOSS_PHASE_SHARE[0] ? 1 : 0;
}

/** A boss state with no boss. */
export function newBossState(): BossState {
  return {
    active: false,
    name: "",
    hp: 0,
    maxHp: 0,
    phase: 0,
    shielded: false,
    telegraph: 0,
    defeatedAtMs: undefined,
    anchor: BOSS_ANCHOR,
  };
}

// ───────────────────────────── the fight ─────────────────────────────

/** The step of the attack cycle: wait, then wind-up, then the shield slam. */
type AttackStep = "wait" | "telegraph" | "slam";

/** One boss fight. The state is public and read-only for a reader. The fight changes it in place. */
export class BossFight {
  readonly state: BossState = newBossState();
  private step: AttackStep = "wait";
  /** Time left in the current step, in game ms. */
  private stepMsLeft = 0;
  private shieldMsLeft = 0;
  private shieldCause: BossShieldCause = "slam";
  /** The stage number of the fight, 1-based. */
  private stage = 1;

  constructor(private readonly sink: BossSink) {}

  /** Remove the boss. The state is the state of a stage with no boss. */
  reset(): void {
    const s = this.state;
    s.active = false;
    s.name = "";
    s.hp = 0;
    s.maxHp = 0;
    s.phase = 0;
    s.shielded = false;
    s.telegraph = 0;
    s.defeatedAtMs = undefined;
    this.step = "wait";
    this.stepMsLeft = 0;
    this.shieldMsLeft = 0;
  }

  /** Start the fight of a stage (1-based) with a full-HP boss in phase 0. */
  start(stageNumber: number): void {
    this.reset();
    const s = this.state;
    this.stage = Math.max(1, Math.trunc(stageNumber));
    s.active = true;
    s.name = bossNameOf(this.stage);
    s.maxHp = bossMaxHp(this.stage);
    s.hp = s.maxHp;
    this.enterWait();
    this.sink.emit({
      kind: "boss_start",
      name: s.name,
      stage: this.stage,
      hp: s.hp,
      maxHp: s.maxHp,
      anchor: s.anchor,
    });
  }

  /**
   * A ball hit on a target. A shielded boss takes no damage. `nowMs` is the game time of the
   * hit. It is kept as the time of the defeat.
   */
  hit(source: BossHitSource, nowMs: number): BossHitResult {
    const s = this.state;
    if (!s.active) return "none";
    const damage = BOSS_DAMAGE[source];
    if (s.shielded) {
      this.sendHit(source, damage, true);
      return "blocked";
    }
    s.hp = Math.max(0, s.hp - damage);
    this.sendHit(source, damage, false);
    if (s.hp <= 0) {
      this.defeat(nowMs);
      return "defeated";
    }
    this.checkPhase();
    return "hit";
  }

  /** Advance the shield and the attack cycle by one frame of `dtMs` game ms. */
  tick(dtMs: number): void {
    const s = this.state;
    if (!s.active || !(dtMs > 0) || !Number.isFinite(dtMs)) return;
    this.tickShield(dtMs);
    this.stepMsLeft -= dtMs;
    if (this.stepMsLeft <= 0) this.nextStep();
    s.telegraph =
      this.step === "telegraph"
        ? Math.min(1, Math.max(0, 1 - this.stepMsLeft / BOSS_TELEGRAPH_MS))
        : 0;
  }

  /**
   * The ball ended. The HP stays for the next ball. The wind-up and the shield stop, and the
   * attack cycle starts again with a full wait. The player loses nothing.
   */
  pause(): void {
    if (!this.state.active) return;
    this.enterWait();
    this.state.telegraph = 0;
    if (this.shieldMsLeft > 0) this.dropShield();
  }

  // ───── internals ─────

  private sendHit(source: BossHitSource, damage: number, blocked: boolean): void {
    const s = this.state;
    this.sink.emit({
      kind: "boss_hit",
      damage,
      hp: s.hp,
      maxHp: s.maxHp,
      blocked,
      source,
    });
  }

  private enterWait(): void {
    this.step = "wait";
    this.stepMsLeft = BOSS_WAIT_MS[this.state.phase] ?? BOSS_WAIT_MS[BOSS_MAX_PHASE];
  }

  private nextStep(): void {
    switch (this.step) {
      case "wait":
        this.step = "telegraph";
        this.stepMsLeft = BOSS_TELEGRAPH_MS;
        this.sink.emit({ kind: "boss_telegraph", ms: BOSS_TELEGRAPH_MS });
        return;
      case "telegraph":
        this.step = "slam";
        this.stepMsLeft = BOSS_SLAM_SHIELD_MS;
        this.raiseShield(BOSS_SLAM_SHIELD_MS, "slam");
        return;
      case "slam":
        this.enterWait();
        return;
    }
  }

  private raiseShield(ms: number, cause: BossShieldCause): void {
    this.shieldMsLeft = Math.max(this.shieldMsLeft, ms);
    this.shieldCause = cause;
    this.state.shielded = true;
    this.sink.emit({ kind: "boss_shield", on: true, ms, cause });
  }

  private dropShield(): void {
    this.shieldMsLeft = 0;
    this.state.shielded = false;
    this.sink.emit({ kind: "boss_shield", on: false, ms: 0, cause: this.shieldCause });
  }

  private tickShield(dtMs: number): void {
    if (this.shieldMsLeft <= 0) return;
    this.shieldMsLeft -= dtMs;
    if (this.shieldMsLeft <= 0) this.dropShield();
  }

  /** A phase change interrupts the wind-up, starts a new wait and turns the shield on. */
  private checkPhase(): void {
    const s = this.state;
    const phase = bossPhaseOf(s.hp, s.maxHp);
    if (phase <= s.phase) return;
    s.phase = phase;
    s.telegraph = 0;
    this.enterWait();
    this.sink.emit({ kind: "boss_phase", phase, hp: s.hp, maxHp: s.maxHp });
    this.raiseShield(BOSS_PHASE_SHIELD_MS, "phase");
  }

  private defeat(nowMs: number): void {
    const s = this.state;
    s.active = false;
    s.shielded = false;
    s.telegraph = 0;
    s.defeatedAtMs = nowMs;
    this.shieldMsLeft = 0;
    this.sink.emit({ kind: "boss_defeat", name: s.name, stage: this.stage });
  }
}
